import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { useAuth } from '@/contexts/AuthContext'
import { LogOut, AlertTriangle, Clock, CheckCircle, Loader2, ShoppingCart, Lock } from 'lucide-react'
import type { Prescription } from '@/types/prescription'

export default function UserDashboard() {
  const { user, logout } = useAuth()
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState<number | null>(null)
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [isReordering, setIsReordering] = useState(false)
  
  // Password change state
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordChanged, setPasswordChanged] = useState(false)

  useEffect(() => {
    fetchPrescriptions()
  }, [])
  
  // Debug function to log what's actually coming from the API
  useEffect(() => {
    if (prescriptions.length > 0) {
      console.log('DEBUG - Prescriptions data:', prescriptions)
      console.log('First prescription details:')
      
      // Check if the fields exist and debug their values
      try {
        console.log('- prescription_expiry_date:', prescriptions[0].prescription_expiry_date)
        console.log('- prescription_expiry_date type:', typeof prescriptions[0].prescription_expiry_date)
        console.log('- expiry_date:', prescriptions[0].expiry_date)
        console.log('- repeats:', prescriptions[0].repeats)
        console.log('- repeats type:', typeof prescriptions[0].repeats)
        
        // Try to convert the dates to make sure they're valid
        if (prescriptions[0].prescription_expiry_date) {
          console.log('Converted prescription_expiry_date:', new Date(prescriptions[0].prescription_expiry_date))
        }
      } catch (error) {
        console.error('Error debugging prescription fields:', error)
      }
    }
  }, [prescriptions])

  const fetchPrescriptions = async () => {
    try {
      const response = await fetch('/api/prescriptions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        setPrescriptions(data)
      }
    } catch (error) {
      console.error('Error fetching prescriptions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectItem = (itemId: number) => {
    // Find the prescription
    const prescription = prescriptions.find(p => p.id === itemId);
    
    // Only allow selection if the prescription has repeats
    if (prescription && 
        prescription.repeats !== undefined && 
        prescription.repeats !== null && 
        prescription.repeats <= 0) {
      console.log("Cannot select prescription with zero repeats:", prescription);
      return; // Exit early - don't allow selecting prescriptions with 0 repeats
    }
    
    // Otherwise proceed with the selection toggle
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleSelectAll = () => {
    // Only allow selecting prescriptions with valid repeats
    const selectablePrescriptions = prescriptions.filter(p => 
      p.repeats === undefined || p.repeats === null || p.repeats > 0
    );
    
    // Get just the IDs
    const selectableIds = selectablePrescriptions.map(p => p.id);
    
    // Check if all selectable prescriptions are already selected
    const allSelectableSelected = selectableIds.every(id => selectedItems.includes(id));
    
    if (allSelectableSelected) {
      // If all are selected, clear the selection
      setSelectedItems([])
    } else {
      // Otherwise select all prescriptions with valid repeats
      setSelectedItems(selectableIds)
    }
  }

  const handleReorder = async (prescriptionId?: number) => {
    // If a single prescription ID is provided, reorder just that one
    // Otherwise use the first selected item as the primary prescription
    const primaryPrescriptionId = prescriptionId || (selectedItems.length > 0 ? selectedItems[0] : null);
    
    if (!primaryPrescriptionId) {
      console.error('No prescriptions selected for reorder');
      return;
    }
    
    // Check if the primary prescription has repeats
    const primaryPrescription = prescriptions.find(p => p.id === primaryPrescriptionId);
    if (primaryPrescription && 
        primaryPrescription.repeats !== undefined && 
        primaryPrescription.repeats !== null && 
        primaryPrescription.repeats <= 0) {
      console.error("Cannot reorder prescription with zero repeats:", primaryPrescription);
      return; // Exit early
    }

    try {
      if (prescriptionId) {
        // Single prescription reorder - set processing state for UI
        setIsProcessing(prescriptionId);
      } else {
        // Multiple prescription reorder
        setIsReordering(true);
      }
      
      // Generate a renewal token for the primary prescription
      const response = await fetch('/api/prescriptions/get-renewal-token', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prescriptionId: primaryPrescriptionId })
      });

      if (!response.ok) {
        throw new Error('Failed to generate renewal token');
      }

      const { token } = await response.json();
      
      // Get valid prescriptions that have repeats
      const validPrescriptions = prescriptions.filter(p => 
        p.repeats === undefined || p.repeats === null || p.repeats > 0
      );
      const validPrescriptionIds = validPrescriptions.map(p => p.id);
      
      // Prepare selected IDs list - include primary prescription and any additionally selected
      // But filter out any items with 0 repeats as an extra safety measure
      const allSelectedIds = [
        primaryPrescriptionId, 
        ...selectedItems.filter(id => 
          id !== primaryPrescriptionId && validPrescriptionIds.includes(id)
        )
      ];
      
      // Go directly to the confirmation page with all selected prescriptions
      window.location.href = `/renew-confirm/${token}?selected=${allSelectedIds.join(',')}`;
      
    } catch (error) {
      console.error('Error generating renewal token:', error);
    } finally {
      setIsProcessing(null);
      setIsReordering(false);
    }
  }

  const getExpiryStatus = (prescription: Prescription) => {
    const today = new Date()
    
    // Use prescription_expiry_date if available, otherwise fall back to expiry_date
    // prescription_expiry_date comes from Shopify notes and is the doctor-set expiry
    const expiryDate = prescription.prescription_expiry_date || prescription.expiry_date
    
    const expiry = new Date(expiryDate)
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    
    if (daysUntilExpiry <= 0) return 'expired'
    if (daysUntilExpiry <= 14) return 'expiring-soon'
    return 'active'
  }
  
  const getDaysLeftUntilExpiry = (expiryDate: string | null | undefined): number => {
    if (!expiryDate) return 0
    const today = new Date()
    const expiry = new Date(expiryDate)
    return Math.max(0, Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)))
  }
  
  // Calculate days until reorder is recommended (based on next_reminder_date)
  const getReorderStatus = (prescription: Prescription) => {
    if (!prescription.next_reminder_date) return { status: 'unknown', message: '' };
    
    const today = new Date();
    const reminderDate = new Date(prescription.next_reminder_date);
    const diffDays = Math.ceil((reminderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    // Already due or overdue
    if (diffDays <= 0) {
      return { 
        status: 'due', 
        message: 'Reorder now',
        className: 'text-red-600 font-medium'
      };
    }
    
    // Due soon (within 7 days)
    if (diffDays <= 7) {
      return { 
        status: 'soon', 
        message: `Reorder in ${diffDays} day${diffDays === 1 ? '' : 's'}`,
        className: 'text-amber-600 font-medium'
      };
    }
    
    // Not due yet
    return { 
      status: 'future', 
      message: `Reorder in ${diffDays} days`,
      className: 'text-green-600' 
    };
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with logout */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Welcome, {user?.first_name}</h1>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsChangePasswordOpen(true)
                setCurrentPassword('')
                setNewPassword('')
                setConfirmPassword('')
                setPasswordError('')
                setPasswordChanged(false)
              }}
              className="flex items-center"
            >
              <Lock className="h-4 w-4 mr-2" />
              Change Password
            </Button>
            <Button variant="ghost" onClick={logout} className="flex items-center">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Active Prescriptions */}
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-blue-600 font-bold text-2xl">My Prescriptions</CardTitle>
            {selectedItems.length > 0 && (
              <Button
                onClick={() => handleReorder()}
                disabled={isReordering}
                className="bg-blue-600"
              >
                {isReordering ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Place Order Now ({selectedItems.length})
                  </>
                )}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-4">Loading prescriptions...</div>
            ) : prescriptions.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No active prescriptions found
              </div>
            ) : (
              <div className="space-y-4">
                {/* Select All Option */}
                <div className="flex items-center justify-between pb-4 border-b">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedItems.length === prescriptions.length && prescriptions.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                    <span className="text-sm text-gray-500">Select All</span>
                  </div>
                  
                  <div className="text-sm text-gray-500 flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    <span>Highlighted items need reordering soon</span>
                  </div>
                </div>
              
                {/* Group prescriptions by reorder status for better organization */}
                {/* Due/Overdue Items - Show at top */}
                {prescriptions
                  .filter(p => {
                    if (!p.next_reminder_date) return false;
                    const today = new Date();
                    const reminderDate = new Date(p.next_reminder_date);
                    const diffDays = Math.ceil((reminderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return diffDays <= 0;
                  })
                  .map(prescription => {
                    const status = getExpiryStatus(prescription);
                    const reorderStatus = getReorderStatus(prescription);
                    return (
                      <div key={prescription.id} 
                           className="p-4 border rounded-lg bg-red-50 border-red-200">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              checked={selectedItems.includes(prescription.id)}
                              onCheckedChange={() => handleSelectItem(prescription.id)}
                              className="mt-1"
                              disabled={status === 'expired' || (prescription.repeats !== undefined && prescription.repeats !== null && prescription.repeats <= 0)}
                            />
                            <div>
                              <h3 className="font-medium">{prescription.product_name}</h3>
                              <div className="flex items-center mt-1">
                                {status === 'expiring-soon' && (
                                  <AlertTriangle className="h-4 w-4 text-yellow-500 mr-1" />
                                )}
                                {status === 'expired' && (
                                  <Clock className="h-4 w-4 text-red-500 mr-1" />
                                )}
                                <p className={`text-sm ${
                                  status === 'expiring-soon' ? 'text-yellow-700' :
                                  status === 'expired' ? 'text-red-700' : 'text-gray-500'
                                }`}>
                                  Expires: {prescription.prescription_expiry_date ? 
                                      new Date(prescription.prescription_expiry_date).toLocaleDateString() : 
                                      new Date(prescription.expiry_date).toLocaleDateString()}
                                  {status !== 'expired' && ` (${getDaysLeftUntilExpiry(prescription.prescription_expiry_date || prescription.expiry_date)} days left)`}
                                </p>
                              </div>
                              {prescription.repeats !== undefined && (
                                <div className="flex items-center mt-1">
                                  <p className={`text-sm font-medium ${prescription.repeats > 0 ? 'text-green-600' : 'text-amber-500'}`}>
                                    {prescription.repeats > 0 ? 
                                      <>Repeats remaining: <span className="font-bold">{prescription.repeats}</span></> : 
                                      'No repeats remaining'}
                                  </p>
                                </div>
                              )}
                              {prescription.last_renewal_date && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Last ordered: {new Date(prescription.last_renewal_date).toLocaleDateString()}
                                </p>
                              )}
                              
                              {/* Add reorder timing information */}
                              {prescription.next_reminder_date && (
                                <div className={`text-sm mt-2 ${reorderStatus.className}`}>
                                  <Clock className="h-3 w-3 inline mr-1" />
                                  {reorderStatus.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            <Badge variant="destructive">
                              Reorder Now
                            </Badge>
                            <Button 
                              variant="outline"
                              onClick={() => handleReorder(prescription.id)}
                              disabled={status === 'expired' || isProcessing === prescription.id || 
                                       (prescription.repeats !== undefined && prescription.repeats !== null && prescription.repeats <= 0)}
                              className="min-w-[100px]"
                            >
                              {isProcessing === prescription.id ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Processing...
                                </>
                              ) : status === 'expired' ? (
                                'Expired'
                              ) : (
                                'Reorder'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                {/* Due Soon Items */}
                {prescriptions
                  .filter(p => {
                    if (!p.next_reminder_date) return false;
                    const today = new Date();
                    const reminderDate = new Date(p.next_reminder_date);
                    const diffDays = Math.ceil((reminderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return diffDays > 0 && diffDays <= 7;
                  })
                  .map(prescription => {
                    const status = getExpiryStatus(prescription);
                    const reorderStatus = getReorderStatus(prescription);
                    return (
                      <div key={prescription.id} 
                           className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              checked={selectedItems.includes(prescription.id)}
                              onCheckedChange={() => handleSelectItem(prescription.id)}
                              className="mt-1"
                              disabled={status === 'expired' || (prescription.repeats !== undefined && prescription.repeats !== null && prescription.repeats <= 0)}
                            />
                            <div>
                              <h3 className="font-medium">{prescription.product_name}</h3>
                              <div className="flex items-center mt-1">
                                {status === 'expiring-soon' && (
                                  <AlertTriangle className="h-4 w-4 text-yellow-500 mr-1" />
                                )}
                                {status === 'expired' && (
                                  <Clock className="h-4 w-4 text-red-500 mr-1" />
                                )}
                                <p className={`text-sm ${
                                  status === 'expiring-soon' ? 'text-yellow-700' :
                                  status === 'expired' ? 'text-red-700' : 'text-gray-500'
                                }`}>
                                  Expires: {new Date(prescription.prescription_expiry_date || prescription.expiry_date).toLocaleDateString()}
                                  {status !== 'expired' && ` (${getDaysLeftUntilExpiry(prescription.prescription_expiry_date || prescription.expiry_date)} days left)`}
                                </p>
                              </div>
                              {prescription.repeats !== undefined && (
                                <div className="flex items-center mt-1">
                                  <p className={`text-sm font-medium ${prescription.repeats > 0 ? 'text-green-600' : 'text-amber-500'}`}>
                                    {prescription.repeats > 0 ? 
                                      <>Repeats remaining: <span className="font-bold">{prescription.repeats}</span></> : 
                                      'No repeats remaining'}
                                  </p>
                                </div>
                              )}
                              {prescription.last_renewal_date && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Last ordered: {new Date(prescription.last_renewal_date).toLocaleDateString()}
                                </p>
                              )}
                              
                              {/* Add reorder timing information */}
                              {prescription.next_reminder_date && (
                                <div className={`text-sm mt-2 ${reorderStatus.className}`}>
                                  <Clock className="h-3 w-3 inline mr-1" />
                                  {reorderStatus.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            <Badge variant="warning">
                              Due Soon
                            </Badge>
                            <Button 
                              variant="outline"
                              onClick={() => handleReorder(prescription.id)}
                              disabled={status === 'expired' || isProcessing === prescription.id || 
                                       (prescription.repeats !== undefined && prescription.repeats !== null && prescription.repeats <= 0)}
                              className="min-w-[100px]"
                            >
                              {isProcessing === prescription.id ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Processing...
                                </>
                              ) : status === 'expired' ? (
                                'Expired'
                              ) : (
                                'Reorder'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                {/* Regular Items */}
                {prescriptions
                  .filter(p => {
                    if (!p.next_reminder_date) return true; // Include items with no reminder date
                    const today = new Date();
                    const reminderDate = new Date(p.next_reminder_date);
                    const diffDays = Math.ceil((reminderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    return diffDays > 7;
                  })
                  .map(prescription => {
                    const status = getExpiryStatus(prescription);
                    const reorderStatus = getReorderStatus(prescription);
                    return (
                      <div key={prescription.id} 
                           className={`p-4 border rounded-lg bg-white ${
                             status === 'expiring-soon' ? 'border-yellow-400 bg-yellow-50' :
                             status === 'expired' ? 'border-red-400 bg-red-50' : ''
                           }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-3">
                            <Checkbox
                              checked={selectedItems.includes(prescription.id)}
                              onCheckedChange={() => handleSelectItem(prescription.id)}
                              className="mt-1"
                              disabled={status === 'expired' || (prescription.repeats !== undefined && prescription.repeats !== null && prescription.repeats <= 0)}
                            />
                            <div>
                              <h3 className="font-medium">{prescription.product_name}</h3>
                              <div className="flex items-center mt-1">
                                {status === 'expiring-soon' && (
                                  <AlertTriangle className="h-4 w-4 text-yellow-500 mr-1" />
                                )}
                                {status === 'expired' && (
                                  <Clock className="h-4 w-4 text-red-500 mr-1" />
                                )}
                                <p className={`text-sm ${
                                  status === 'expiring-soon' ? 'text-yellow-700' :
                                  status === 'expired' ? 'text-red-700' : 'text-gray-500'
                                }`}>
                                  Expires: {new Date(prescription.prescription_expiry_date || prescription.expiry_date).toLocaleDateString()}
                                  {status !== 'expired' && ` (${getDaysLeftUntilExpiry(prescription.prescription_expiry_date || prescription.expiry_date)} days left)`}
                                </p>
                              </div>
                              {prescription.repeats !== undefined && (
                                <div className="flex items-center mt-1">
                                  <p className={`text-sm font-medium ${prescription.repeats > 0 ? 'text-green-600' : 'text-amber-500'}`}>
                                    {prescription.repeats > 0 ? 
                                      <>Repeats remaining: <span className="font-bold">{prescription.repeats}</span></> : 
                                      'No repeats remaining'}
                                  </p>
                                </div>
                              )}
                              {prescription.last_renewal_date && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Last ordered: {new Date(prescription.last_renewal_date).toLocaleDateString()}
                                </p>
                              )}
                              
                              {/* Add reorder timing information */}
                              {prescription.next_reminder_date && (
                                <div className={`text-sm mt-2 ${reorderStatus.className}`}>
                                  <Clock className="h-3 w-3 inline mr-1" />
                                  {reorderStatus.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            <Badge variant="outline">
                              Active
                            </Badge>
                            <Button 
                              variant="outline"
                              onClick={() => handleReorder(prescription.id)}
                              disabled={status === 'expired' || isProcessing === prescription.id || 
                                       (prescription.repeats !== undefined && prescription.repeats !== null && prescription.repeats <= 0)}
                              className="min-w-[100px]"
                            >
                              {isProcessing === prescription.id ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Processing...
                                </>
                              ) : status === 'expired' ? (
                                'Expired'
                              ) : (
                                'Reorder'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
            
            {/* Footer with selected items summary */}
            {selectedItems.length > 0 && (
              <div className="mt-6 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold">Selected Items: {selectedItems.length}</p>
                    <p className="text-sm text-gray-500">Click the button to reorder all selected items together</p>
                  </div>
                  <Button
                    onClick={() => handleReorder()}
                    disabled={isReordering}
                    className="bg-blue-600"
                  >
                    {isReordering ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        Complete Your Order
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Recent Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {prescriptions
                .filter(p => p.last_renewal_date)
                .sort((a, b) => new Date(b.last_renewal_date!).getTime() - new Date(a.last_renewal_date!).getTime())
                .slice(0, 5)
                .map(prescription => (
                  <div key={`order-${prescription.id}`} className="p-4 border rounded-lg bg-white">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium">{prescription.product_name}</h3>
                        <p className="text-sm text-gray-500">
                          Ordered: {new Date(prescription.last_renewal_date!).toLocaleDateString()}
                        </p>
                      </div>
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Help Section */}
        <Card>
          <CardHeader>
            <CardTitle>Need Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              For any questions about your prescriptions or if you need assistance, 
              please contact us at:
            </p>
            <div className="mt-4">
              <a href="mailto:support@hydralyfe.com.au" 
                 className="text-blue-600 hover:underline">
                support@hydralyfe.com.au
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Change Your Password</DialogTitle>
            <DialogDescription>
              Enter your current password and your new password below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="current-password" className="text-sm font-medium">
                Current Password
              </label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="new-password" className="text-sm font-medium">
                New Password
              </label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="confirm-password" className="text-sm font-medium">
                Confirm New Password
              </label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
              />
            </div>
            {passwordError && (
              <div className="text-red-500 text-sm">{passwordError}</div>
            )}
            {passwordChanged && (
              <div className="text-green-500 text-sm">Password changed successfully!</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsChangePasswordOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                // Reset states
                setPasswordError('')
                setPasswordChanged(false)
                
                // Validate passwords
                if (!currentPassword) {
                  setPasswordError('Current password is required')
                  return
                }
                
                if (!newPassword) {
                  setPasswordError('New password is required')
                  return
                }
                
                if (newPassword !== confirmPassword) {
                  setPasswordError('New passwords do not match')
                  return
                }
                
                if (newPassword.length < 6) {
                  setPasswordError('New password must be at least 6 characters')
                  return
                }
                
                try {
                  setIsChangingPassword(true)
                  
                  const response = await fetch('/api/auth/change-password', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({ 
                      currentPassword, 
                      newPassword 
                    })
                  })
                  
                  const data = await response.json()
                  
                  if (!response.ok) {
                    setPasswordError(data.message || 'Failed to change password')
                    return
                  }
                  
                  // Success
                  setPasswordChanged(true)
                  setCurrentPassword('')
                  setNewPassword('')
                  setConfirmPassword('')
                  
                  // Close dialog after 2 seconds
                  setTimeout(() => {
                    setIsChangePasswordOpen(false)
                  }, 2000)
                  
                } catch (error) {
                  console.error('Error changing password:', error)
                  setPasswordError('An error occurred. Please try again.')
                } finally {
                  setIsChangingPassword(false)
                }
              }}
              disabled={isChangingPassword}
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
} 